import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useToken } from "@/hooks/useToken";
import { useConsent } from "@/hooks/useConsent";
import { ConsentType } from "@/types/consent";
import { Button } from "@/components/ui/atoms/button";
import { membersService } from "@/services/members.service";
import type { Member } from "@/types/member";
import memberPermissionService from "@/services/member-permissions.service";

const GrantAccessPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const query = new URLSearchParams(location.search);
  const token = query.get("token") || "";

  const {
    tokenData,
    loading: tokenLoading,
    error: tokenError,
  } = useToken(token);
  const {
    consent,
    loading: consentLoading,
    error: consentError,
    acceptConsent,
    declineConsent,
  } = useConsent(token, ConsentType.MANAGE_MEMBER);

  const [managedMember, setManagedMember] = useState<Member | null>(null);
  const [invitedMember, setInvitedMember] = useState<Member | null>(null);
  const [membersLoading, setMembersLoading] = useState(true);
  const [success, setSuccess] = useState<false | "accepted" | "declined">(
    false
  );

  useEffect(() => {
    let isMounted = true;
    if (!token) {
      navigate("/");
      return;
    }
    const fetchMembers = async () => {
      if (tokenData?.token_params) {
        const familyMemberId = tokenData.token_params.familyMemberId;
        const memberId = tokenData.memberId;
        setMembersLoading(true);
        try {
          let managed: Member | null = null;
          let invited: Member | null = null;
          if (familyMemberId) {
            managed = await membersService.getMember(familyMemberId);
          }
          if (memberId) {
            invited = await membersService.getMember(memberId);
          }
          if (isMounted) {
            setManagedMember(managed);
            setInvitedMember(invited);
          }
        } catch {
          if (isMounted) {
            setManagedMember(null);
            setInvitedMember(null);
          }
        } finally {
          if (isMounted) setMembersLoading(false);
        }
      } else {
        setMembersLoading(false);
      }
    };

    fetchMembers();
    return () => {
      isMounted = false;
    };
  }, [token, tokenData, navigate]);

  if (tokenError || consentError)
    return <div className="text-red-500 p-4">{tokenError || consentError}</div>;
  if (!consent)
    return <div className="p-4">No consent found or already handled.</div>;

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
      <div className="bg-white rounded-xl shadow-md p-8 max-w-lg w-full mt-12">
        <h1 className="text-2xl font-bold mb-2 text-violet-700">
          {consent.title || consent.content?.title || "Grant Access Consent"}
        </h1>
        <div className="mb-3 text-sm text-gray-700">
          {membersLoading ? (
            <div className="italic text-gray-400">
              Loading member details...
            </div>
          ) : invitedMember && managedMember ? (
            <div>
              <span>
                <strong>
                  Hello {invitedMember.first_name} {invitedMember.last_name}
                </strong>
                , <br /> <br />
                You are given access to manage{" "}
                <strong>
                  {managedMember.first_name} {managedMember.last_name}
                </strong>
                's account and information within your family group.
              </span>
            </div>
          ) : (
            <div className="italic text-gray-400">
              Member details could not be loaded. Please check your invite link
              or contact support.
            </div>
          )}
        </div>
        <div className="border rounded p-4 mb-6 bg-violet-50">
          {consent.content?.description}
        </div>
        <div className="flex gap-4">
          {success ? (
            <div className="w-full text-center font-semibold text-lg py-6">
              {success === "declined" ? (
                <span className="text-red-700">
                  You have denied the access. You won't be able to manage this
                  family member.
                </span>
              ) : (
                <span className="text-green-700">
                  You have been granted access to manage{" "}
                  <strong>
                    {managedMember?.first_name} {managedMember?.last_name}
                  </strong>
                  's account and information.
                  <br />
                  You can now close this page.
                </span>
              )}
            </div>
          ) : (
            <>
              <Button
                className="text-#4C189A text-white font-semibold px-6"
                onClick={async () => {
                  if (invitedMember && managedMember && tokenData) {
                    console.log(tokenData);
                    await memberPermissionService.acceptGrantAccess({
                      memberId: invitedMember.id,
                      familyId: tokenData.family_id,
                      familyMemberId: managedMember.id,
                      roleId: tokenData.role_id,
                      skipTokenDelete: true,
                    });
                  }
                  setSuccess("accepted");
                }}
              >
                Accept
              </Button>
              <Button
                className="bg-red-500 hover:bg-red-600 text-white font-semibold px-6"
                variant="outline"
                onClick={async () => {
                  if (tokenData && invitedMember && managedMember) {
                    await memberPermissionService.declineGrantAccess({
                      tokenData,
                      invitedMember,
                      managedMember,
                    });
                  }
                  setSuccess("declined");
                }}
              >
                Decline
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GrantAccessPage;
